import { useCallback, useRef, useState } from 'react';
import { useTimer } from 'src/utils/hooks/use-timer';

const leadingZero = (num: number): string => (num < 10 ? `0${num}` : `${num}`);

const formatTime = (countDown: number) => {
  const hours = Math.floor(
    (countDown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const minutes = Math.floor((countDown % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((countDown % (1000 * 60)) / 1000);

  return {
    hours: leadingZero(hours),
    minutes: leadingZero(minutes),
    seconds: leadingZero(seconds),
  };
};

type UseOptimizedCountdownProps = {
  targetDate: number;
  onComplete?: () => void;
  delay?: number;
};

export const useOptimizedCountdown = ({
  targetDate,
  onComplete,
  delay = 1000,
}: UseOptimizedCountdownProps) => {
  const initialCountDown = targetDate - Date.now();
  const [countDown, setCountDown] = useState(initialCountDown);
  const onCompleteCalledRef = useRef(false);
  const targetDateRef = useRef(targetDate);

  if (targetDateRef.current !== targetDate) {
    targetDateRef.current = targetDate;
    onCompleteCalledRef.current = false;
    const newCountDown = targetDate - Date.now();
    setCountDown(newCountDown);
  }

  const handleComplete = useCallback(() => {
    if (!onCompleteCalledRef.current && onComplete) {
      onCompleteCalledRef.current = true;
      onComplete();
    }
  }, [onComplete]);

  const { timer, isPause } = useTimer({
    delay,
    startImmediately: initialCountDown > 0,
    callback: (elapsedTime) => {
      const currentTime = initialCountDown - elapsedTime;

      if (currentTime <= 0) {
        setCountDown(0);
        handleComplete();
      } else {
        setCountDown(currentTime);
      }
    },
  });

  const isComplete = countDown <= 0;
  const formattedTime = formatTime(Math.max(0, countDown));

  return {
    ...formattedTime,
    timeLeft: Math.max(0, countDown),
    isComplete,
    isActive: !isComplete && countDown > 0,
    isPaused: isPause,
    elapsedTime: timer,
  };
};
